"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { calcPythagoreanSquare, CELL_MEANINGS, type PythagoreanResult } from "@/lib/numerology/pythagorean-square";

// Grid layout: [digit, row, col]
const GRID: [number, number, number][] = [
  [1, 0, 0], [4, 0, 1], [7, 0, 2],
  [2, 1, 0], [5, 1, 1], [8, 1, 2],
  [3, 2, 0], [6, 2, 1], [9, 2, 2],
];

function getMeaning(digit: number, count: number): string {
  const m = CELL_MEANINGS[digit];
  if (!m) return "";
  if (count === 0) return m.noDigits;
  if (count === 1) return m.oneDigit;
  if (count === 2) return m.twoDigits;
  if (count === 3) return m.threeDigits;
  return m.manyDigits;
}

function Dots({ count }: { count: number }) {
  const filled = Math.min(count, 6);
  return (
    <div className="flex flex-wrap justify-center gap-0.5 min-h-[20px] items-center">
      {filled === 0 ? (
        <span className="w-3 h-3 rounded-full bg-stone-200 inline-block" />
      ) : (
        Array.from({ length: filled }).map((_, i) => (
          <span
            key={i}
            className="w-3 h-3 rounded-full bg-violet-500 inline-block"
          />
        ))
      )}
      {count > 6 && (
        <span className="text-[10px] text-violet-700 font-bold">+{count - 6}</span>
      )}
    </div>
  );
}

interface CellProps {
  digit: number;
  count: number;
}

function Cell({ digit, count }: CellProps) {
  const isEmpty = count === 0;
  const isHighlighted = count >= 3;

  return (
    <div
      className={[
        "flex flex-col items-center justify-between rounded-xl border p-2 sm:p-3 aspect-square",
        isEmpty
          ? "bg-stone-50 border-stone-200"
          : isHighlighted
          ? "bg-violet-50 border-violet-300"
          : "bg-white border-stone-200",
      ].join(" ")}
    >
      <span className={`text-xs font-bold ${isEmpty ? "text-stone-400" : "text-violet-700"}`}>
        {digit}
      </span>
      <Dots count={count} />
      <span className={`text-[9px] sm:text-[10px] text-center leading-tight font-medium ${isEmpty ? "text-stone-400" : "text-stone-600"}`}>
        {CELL_MEANINGS[digit]?.name}
      </span>
    </div>
  );
}

function WorkingNumbers({ result }: { result: PythagoreanResult }) {
  const t = useTranslations("pythagoreanCalculator");
  const items = [
    { label: t("wnLabel", { n: 1 }), value: result.wn1, hint: t("wnHint", { n: 1 }) },
    { label: t("wnLabel", { n: 2 }), value: result.wn2, hint: t("wnHint", { n: 2 }) },
    { label: t("wnLabel", { n: 3 }), value: result.wn3, hint: t("wnHint", { n: 3 }) },
    { label: t("wnLabel", { n: 4 }), value: result.wn4, hint: t("wnHint", { n: 4 }) },
  ];
  return (
    <div className="flex gap-3 flex-wrap justify-center">
      {items.map(({ label, value, hint }) => (
        <div key={label} className="flex flex-col items-center bg-violet-50 border border-violet-100 rounded-xl px-4 py-2">
          <span className="text-xs text-violet-500 font-semibold">{label}</span>
          <span className="text-xl font-bold text-violet-800">{value}</span>
          <span className="text-[10px] text-stone-400">{hint}</span>
        </div>
      ))}
    </div>
  );
}

function Interpretations({ result }: { result: PythagoreanResult }) {
  const t = useTranslations("pythagoreanCalculator");
  const digits = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const active = digits.filter((d) => result.counts[d] > 0);

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-stone-800 text-sm">{t("cellsBreakdown")}</h3>
      {active.map((digit) => {
        const count = result.counts[digit];
        const meaning = CELL_MEANINGS[digit];
        return (
          <div
            key={digit}
            className="rounded-xl border border-stone-100 bg-white p-4"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center shrink-0">
                {digit}
              </span>
              <span className="font-semibold text-stone-900 text-sm">{meaning.name}</span>
              <span className="ml-auto text-xs text-violet-500 font-medium">{t("digitsCount", { count })}</span>
            </div>
            <p className="text-sm text-stone-600 leading-relaxed">{getMeaning(digit, count)}</p>
          </div>
        );
      })}
      {digits
        .filter((d) => result.counts[d] === 0)
        .map((digit) => {
          const meaning = CELL_MEANINGS[digit];
          return (
            <div
              key={digit}
              className="rounded-xl border border-dashed border-stone-200 bg-stone-50 p-4"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-6 h-6 rounded-full bg-stone-200 text-stone-400 text-xs font-bold flex items-center justify-center shrink-0">
                  {digit}
                </span>
                <span className="font-semibold text-stone-400 text-sm">{meaning.name}</span>
                <span className="ml-auto text-xs text-stone-400">{t("empty")}</span>
              </div>
              <p className="text-sm text-stone-500 leading-relaxed">{getMeaning(digit, 0)}</p>
            </div>
          );
        })}
    </div>
  );
}

const DATE_RE = /^(\d{2})\.(\d{2})\.(\d{4})$/;

function validateDate(value: string): string | null {
  const match = value.match(DATE_RE);
  if (!match) return "errFormat";
  const [, dd, mm, yyyy] = match;
  const day = parseInt(dd, 10);
  const month = parseInt(mm, 10);
  const year = parseInt(yyyy, 10);
  if (month < 1 || month > 12) return "errMonth";
  if (day < 1 || day > 31) return "errDay";
  if (year < 1900 || year > 2100) return "errYear";
  return null;
}

export default function PythagoreanCalculator() {
  const t = useTranslations("pythagoreanCalculator");
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PythagoreanResult | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errKey = validateDate(input.trim());
    if (errKey) {
      setError(t(errKey));
      return;
    }
    setError(null);
    setResult(calcPythagoreanSquare(input.trim()));
  }

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-6">
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="DD.MM.AAAA"
            maxLength={10}
            className={[
              "w-full rounded-xl border px-4 py-3 text-base font-mono outline-none transition",
              "focus:ring-2 focus:ring-violet-400 focus:border-violet-400",
              error ? "border-red-400 bg-red-50" : "border-stone-300 bg-white",
            ].join(" ")}
            aria-label={t("birthDate")}
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
        <button
          type="submit"
          className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold px-6 py-3 transition shrink-0"
        >
          {t("calculate")}
        </button>
      </form>

      {result && (
        <div className="space-y-6">
          {/* 3×3 Grid */}
          <div>
            <h3 className="font-semibold text-stone-800 text-sm mb-3 text-center">{t("psychomatrix")}</h3>
            <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
              {GRID.map(([digit]) => (
                <Cell key={digit} digit={digit} count={result.counts[digit]} />
              ))}
            </div>
          </div>

          {/* Working numbers */}
          <WorkingNumbers result={result} />

          {/* Interpretations */}
          <Interpretations result={result} />
        </div>
      )}
    </div>
  );
}
